import { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Navbar from "layouts/ecommerce/posnavbar/Navbar";
import { PosCustomButton } from "../../../../customComponents/PosCustomButton/PosCustomButton";
import CustomTableGrid from "../../../../customComponents/CustomTableGrid/CustomTableGrid";
import "../index.css";
import "./prepRequest-kitchenOS.css";
import CustomPopupModal from "../../../../customComponents/CustomPopupModal/CustomPopupModal";
import PosCustomDropdown from "../../../../customComponents/PosCustomDropdown/PosCustomDropdown";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CustomFilterComponent from "../../../../customComponents/CustomFilterComponent";
import { MOCK_OUTLET_OPTIONS, MOCK_PREP_SLOTS, MOCK_PREP_STORES, MOCK_PREP_ITEMS, MOCK_LOCATION_OPTIONS } from "../MOCK_DATA_KOS";

const PRIORITY_OPTIONS = [
  { label: "High", value: "high" },
  { label: "Medium", value: "medium" },
  { label: "Low", value: "low" },
];

const PREP_SLOT_OPTIONS = [
  { label: "Morning Prep (5am - 9am)", value: "morning" },
  { label: "Lunch Prep (10am - 2pm)", value: "lunch" },
  { label: "Dinner Prep (1pm - 5pm)", value: "dinner" },
];

const UNIT_OPTIONS = [
  { label: "Units", value: "units" },
  { label: "kg", value: "kg" },
  { label: "L", value: "L" },
  { label: "g", value: "g" },
];

const TABS = ["Consolidated by Slot", "Consolidated by Stores", "Consolidated by Item"];

const FILTER_CONFIG = [

  {
    key: "requestingOutlet",
    label: "Requesting Outlet",
    options: MOCK_LOCATION_OPTIONS,
  },
  {
    key: "priority",
    label: "Priority",
    options: [
      { label: "High", value: "High" },
      { label: "Medium", value: "Medium" },
      { label: "Low", value: "Low" },
    ],
  },
  {
    key: "prepSlot",
    label: "Prep Slot",
    options: [
      { label: "Morning", value: "Morning" },
      { label: "Lunch", value: "Lunch" },
      { label: "Dinner", value: "Dinner" },
    ],
  },
];

const EMPTY_FILTERS = { requestingOutlet: [], priority: [], prepSlot: [] };

// ─── Helpers ──────────────────────────────────────────────────────────────────

function PriorityBadge({ priority }) {
  return <span className={`prepR_kos_badge prepR_kos_badge-${priority.toLowerCase()}`}>{priority}</span>;
}

PriorityBadge.propTypes = {
  priority: PropTypes.string.isRequired,
};

// ─── Column Definitions ───────────────────────────────────────────────────────

const SLOT_COLUMNS = [
  { key: "name", label: "Item Name" },
  { key: "prepTime", label: "Prep Time", render: (row) => <span className="prepR_kos_time">{row.prepTime}</span> },
  { key: "targetTime", label: "Target Time", render: (row) => <span className="prepR_kos_time">{row.targetTime}</span> },
  { key: "priority", label: "Priority", render: (row) => <PriorityBadge priority={row.priority} /> },
  { key: "qty", label: "Total Requested", render: (row) => <><strong>{row.qty}</strong> <span className="prepR_kos_unit">{row.unit}</span></> },
  { key: "action", label: "Action", render: () => <button className="prepR_kos_review-btn">Review</button> },
];

const STORE_COLUMNS = [
  { key: "name", label: "Item Name" },
  { key: "prepSlot", label: "Prep Slot", render: (row) => <span className="prepR_kos_slot-label">{row.prepSlot}</span> },
  { key: "targetTime", label: "Target Time", render: (row) => <span className="prepR_kos_time">{row.targetTime}</span> },
  { key: "priority", label: "Priority", render: (row) => <PriorityBadge priority={row.priority} /> },
  { key: "qty", label: "Requested Qty", render: (row) => <><strong>{row.qty}</strong> <span className="prepR_kos_unit">{row.unit}</span></> },
  { key: "action", label: "Action", render: () => <button className="prepR_kos_review-btn">Review</button> },
];

const ITEM_COLUMNS = [
  { key: "name", label: "Item Name" },
  { key: "totalNeeded", label: "Total Needed", render: (row) => <><strong>{row.totalNeeded}</strong> <span className="prepR_kos_unit">{row.unit}</span></> },
  { key: "outlets", label: "Outlets" },
  { key: "prepStart", label: "Prep Start", render: (row) => <span className="prepR_kos_time">{row.prepStart}</span> },
  { key: "targetTime", label: "Target Time", render: (row) => <span className="prepR_kos_time">{row.targetTime}</span> },
  { key: "priority", label: "Priority", render: (row) => <PriorityBadge priority={row.priority} /> },
  { key: "slot", label: "Slot", render: (row) => <span className="prepR_kos_slot-label">{row.slot}</span> },
  { key: "action", label: "Action", render: () => <button className="prepR_kos_review-btn">Review</button> },
];

// ─── Tab Views ────────────────────────────────────────────────────────────────

function BySlotView({ slots }) {
  if (slots?.length === 0) {
    return <p className="prepR_kos_no-results">No results match the selected filters.</p>;
  }
  return (
    <div className="prepR_kos_groups">
      {slots?.map((group) => (
        <div key={group.slot} className="prepR_kos_group">
          <div className="prepR_kos_group-header">
            <div>
              <p className="prepR_kos_group-title">{group.slot}</p>
              <p className="prepR_kos_group-sub">{group.items?.length} items scheduled for preparation.</p>
            </div>
            <PosCustomButton
              title={`Approve All for ${group.slot.replace(" Prep Slot", "")}`}
              variant="green-d"
              p="6px 14px"
              fontSize="13px"
              border
            />
          </div>
          <CustomTableGrid columns={SLOT_COLUMNS} rows={group.items} rowKey="name" />
        </div>
      ))}
    </div>
  );
}

BySlotView.propTypes = { slots: PropTypes.array.isRequired };

function ByStoresView({ stores }) {
  if (stores?.length === 0) {
    return <p className="prepR_kos_no-results">No results match the selected filters.</p>;
  }
  return (
    <div className="prepR_kos_groups">
      {stores?.map((group) => (
        <div key={group.store} className="prepR_kos_group">
          <div className="prepR_kos_group-header">
            <div>
              <p className="prepR_kos_group-title">{group.store}</p>
              <p className="prepR_kos_group-sub">{group.items?.length} items requested by this location.</p>
            </div>
            <PosCustomButton
              title={`Approve All for ${group.store}`}
              variant="green-d"
              p="6px 14px"
              fontSize="13px"
              border
            />
          </div>
          <CustomTableGrid columns={STORE_COLUMNS} rows={group.items} rowKey="name" />
        </div>
      ))}
    </div>
  );
}

ByStoresView.propTypes = { stores: PropTypes.array.isRequired };

function ByItemView({ items }) {
  if (items?.length === 0) {
    return <p className="prepR_kos_no-results">No results match the selected filters.</p>;
  }
  return (
    <div className="prepR_kos_group">
      <CustomTableGrid columns={ITEM_COLUMNS} rows={items} rowKey="name" />
    </div>
  );
}

ByItemView.propTypes = { items: PropTypes.array.isRequired };

// ─── New Request Modal ────────────────────────────────────────────────────────

function NewRequestModal({ open, onClose }) {
  const [outlet, setOutlet] = useState({});
  const [targetDateTime, setTargetDateTime] = useState("");
  const [priority, setPriority] = useState({});
  const [prepSlot, setPrepSlot] = useState({});
  const [items, setItems] = useState([{ id: Date.now(), name: "", qty: 0, unit: {} }]);

  function handleClose() {
    setOutlet({});
    setTargetDateTime("");
    setPriority({});
    setPrepSlot({});
    setItems([{ id: Date.now(), name: "", qty: 0, unit: {} }]);
    onClose();
  }

  function addItem() {
    setItems((prev) => [...prev, { id: Date.now(), name: "", qty: 0, unit: {} }]);
  }

  function removeItem(id) {
    setItems((prev) => prev?.filter((item) => item.id !== id));
  }

  function updateItem(id, field, value) {
    setItems((prev) => prev?.map((item) => (item.id === id ? { ...item, [field]: value } : item)));
  }

  return (
    <CustomPopupModal
      open={open}
      onClose={handleClose}
      title="New Prep Request"
      width="640px"
      maxHeight="75vh"
      footer={
        <PosCustomButton
          title={`Submit Request (${items?.length} items)`}
          variant="blue-d"
          p="8px 20px"
          fontSize="13px"
        />
      }
    >
      <p className="prepR_kos_nrm-subtitle">Submit a new request for menu items or prep items.</p>

      <div className="prepR_kos_nrm-grid-2">
        <div className="prepR_kos_nrm-field">
          <label className="prepR_kos_nrm-label">Requesting Outlet</label>
          <PosCustomDropdown
            dropdownData={MOCK_OUTLET_OPTIONS}
            placeholder="Select outlet"
            selectedData={outlet}
            setSelectedData={setOutlet}
            width="100%"
            enableScrollEffect={false}
          />
        </div>
        <div className="prepR_kos_nrm-field">
          <label className="prepR_kos_nrm-label">Target Date / Time</label>
          <input
            type="datetime-local"
            className="prepR_kos_nrm-input"
            value={targetDateTime}
            onChange={(e) => setTargetDateTime(e.target.value)}
          />
        </div>
        <div className="prepR_kos_nrm-field">
          <label className="prepR_kos_nrm-label">Overall Priority</label>
          <PosCustomDropdown
            dropdownData={PRIORITY_OPTIONS}
            placeholder="Select priority"
            selectedData={priority}
            setSelectedData={setPriority}
            width="100%"
            enableScrollEffect={false}
          />
        </div>
        <div className="prepR_kos_nrm-field">
          <label className="prepR_kos_nrm-label">Prep Slot</label>
          <PosCustomDropdown
            dropdownData={PREP_SLOT_OPTIONS}
            placeholder="Select prep slot"
            selectedData={prepSlot}
            setSelectedData={setPrepSlot}
            width="100%"
            enableScrollEffect={false}
          />
        </div>
      </div>

      <div className="prepR_kos_nrm-items-section">
        <p className="prepR_kos_nrm-items-title">Requested Items</p>
        {items?.map((item) => (
          <div key={item.id} className="prepR_kos_nrm-item-row">
            <div className="prepR_kos_nrm-field">
              <label className="prepR_kos_nrm-label">Item Name</label>
              <input
                type="text"
                className="prepR_kos_nrm-input"
                placeholder="Search item (e.g. Samosa, Dough, Flour)..."
                value={item.name}
                onChange={(e) => updateItem(item.id, "name", e.target.value)}
              />
            </div>
            <div className="prepR_kos_nrm-field">
              <label className="prepR_kos_nrm-label">Quantity</label>
              <input
                type="number"
                className="prepR_kos_nrm-input"
                min="0"
                value={item.qty}
                onChange={(e) => updateItem(item.id, "qty", e.target.value)}
              />
            </div>
            <div className="prepR_kos_nrm-field">
              <label className="prepR_kos_nrm-label">Unit</label>
              <PosCustomDropdown
                dropdownData={UNIT_OPTIONS}
                placeholder="Unit"
                selectedData={item.unit}
                setSelectedData={(data) => updateItem(item.id, "unit", data)}
                width="120px"
                enableScrollEffect={false}
              />
            </div>
            <button
              className="prepR_kos_nrm-delete-btn"
              onClick={() => removeItem(item.id)}
              style={{ visibility: items?.length === 1 ? "hidden" : "visible" }}
              aria-label="Remove item"
            >
              <DeleteOutlineIcon fontSize="small" />
            </button>
          </div>
        ))}
        <button className="prepR_kos_nrm-add-item-btn" onClick={addItem}>
          + Add Another Item
        </button>
      </div>
    </CustomPopupModal>
  );
}

NewRequestModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function PrepRequestKitchenOS() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [isNewRequestOpen, setIsNewRequestOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState(EMPTY_FILTERS);

  const activeFilterCount = useMemo(
    () => Object?.values(selectedFilters)?.reduce((sum, arr) => sum + arr?.length, 0),
    [selectedFilters]
  );

  const filteredSlots = useMemo(() => {
    const { priority, prepSlot } = selectedFilters;
    return MOCK_PREP_SLOTS?.filter((group) => {
      if (prepSlot?.length === 0) return true;
      const slotName = group.slot.replace(" Prep Slot", "");
      return prepSlot?.includes(slotName);
    })
      ?.map((group) => ({
        ...group,
        items: priority?.length === 0 ? group.items : group.items?.filter((item) => priority?.includes(item.priority)),
      }))
      ?.filter((group) => group.items?.length > 0);
  }, [selectedFilters]);

  const filteredStores = useMemo(() => {
    const { requestingOutlet, priority, prepSlot } = selectedFilters;
    return MOCK_PREP_STORES?.filter((group) => {
      if (requestingOutlet?.length === 0) return true;
      return requestingOutlet?.includes(group.store);
    })
      ?.map((group) => ({
        ...group,
        items: group.items?.filter((item) => {
          if (priority?.length > 0 && !priority?.includes(item.priority)) return false;
          if (prepSlot?.length > 0 && !prepSlot?.includes(item.prepSlot)) return false;
          return true;
        }),
      }))
      ?.filter((group) => group.items?.length > 0);
  }, [selectedFilters]);

  const filteredItems = useMemo(() => {
    const { priority, prepSlot } = selectedFilters;
    return MOCK_PREP_ITEMS?.filter((item) => {
      if (priority?.length > 0 && !priority?.includes(item.priority)) return false;
      if (prepSlot?.length > 0 && !prepSlot?.includes(item.slot)) return false;
      return true;
    });
  }, [selectedFilters]);

  return (
    <Navbar pageTitle="Kitchen OS">
      <div className="idx_kos_wrapper">
        <div className="prepR_kos_page">
          {/* Header */}
          <div className="prepR_kos_header">
            <div>
              <p className="prepR_kos_title">Prep Requests</p>
              <p className="prepR_kos_subtitle">
                Review and approve aggregated item requests from all outlets to generate prep schedules.
              </p>
            </div>
            <div className="prepR_kos_header-actions">
              <PosCustomButton title="Compute Inventory" variant="blue-s" p="7px 14px" fontSize="13px" border onClickFunction={() => navigate("/products/kitchenos/inventory/compute")} />
              <div className="prepR_kos_filter-btn-wrapper">
                <PosCustomButton
                  title="Filter"
                  variant="black-s"
                  p="7px 14px"
                  fontSize="13px"
                  border
                  onClickFunction={() => setFilterOpen(true)}
                />
                {activeFilterCount > 0 && (
                  <span className="prepR_kos_filter-badge">{activeFilterCount}</span>
                )}
              </div>
              <PosCustomButton title="+ New Request" variant="black-s" p="7px 14px" fontSize="13px" border onClickFunction={() => setIsNewRequestOpen(true)} />
              <PosCustomButton title="Auto-Approve All" variant="green-d" p="7px 14px" fontSize="13px" border />
            </div>
          </div>

          {/* Tabs */}
          <div className="prepR_kos_tabs">
            {TABS?.map((tab, i) => (
              <button
                key={tab}
                className={`prepR_kos_tab ${activeTab === i ? "prepR_kos_tab-active" : ""}`}
                onClick={() => setActiveTab(i)}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Content */}
          {activeTab === 0 && <BySlotView slots={filteredSlots} />}
          {activeTab === 1 && <ByStoresView stores={filteredStores} />}
          {activeTab === 2 && <ByItemView items={filteredItems} />}
        </div>
      </div>

      <NewRequestModal open={isNewRequestOpen} onClose={() => setIsNewRequestOpen(false)} />

      <CustomFilterComponent
        open={filterOpen}
        handleClose={() => setFilterOpen(false)}
        filterConfig={FILTER_CONFIG}
        selectedFilters={selectedFilters}
        onApplyFilter={setSelectedFilters}
        variant="panel"
      />
    </Navbar>
  );
}
